import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  useGetRoleTagsQuery,
  useGetRolesQuery,
  useGetMyRolesQuery,
  useActivateMyRoleMutation,
  useDeactivateMyRoleMutation,
  RoleTag,
} from "../../../app/services/roles";
import { roleStatusLabel } from "../../../components/offer-wizard/roleAcceptPosition";
import FilterChipBar from "../../../components/filter-chip/FilterChipBar";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../../components/block/TitleBlock";
import RadioCard from "../../../components/form-item/RadioCard";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import StatusIndicator, { StatusType } from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading";
import type { ShotTag } from "../../../app/services/projects";

const RoleCardTitleRow = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})`
  flex-wrap: wrap;
`;

const RoleCardDescription = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const EmptyState = styled.div.attrs({ className: "bodytext-13" })`
  padding: 24px 0;
  text-align: center;
  color: var(--soremo-light-gray);
`;

/** RoleTag → FilterChipBar に渡す ShotTag 形へのアダプタ。 */
const toShotTagShape = (tag: RoleTag): ShotTag => ({
  id: String(tag.id),
  name: tag.name,
  sort_rule: "newest",
  position: tag.order,
});

const Role: React.FC = () => {
  const { data: tagsData } = useGetRoleTagsQuery();
  const [selectedTagIdStr, setSelectedTagIdStr] = useState<string | null>(null);

  const selectedTagId = useMemo(
    () => (selectedTagIdStr === null ? undefined : Number.parseInt(selectedTagIdStr, 10)),
    [selectedTagIdStr],
  );

  const { data: rolesData, isFetching: isFetchingRoles } = useGetRolesQuery({
    tag_id: selectedTagId,
    limit: 100,
  });

  const { data: myRolesData, isLoading: isLoadingMyRoles } = useGetMyRolesQuery();

  const [activateMyRole] = useActivateMyRoleMutation();
  const [deactivateMyRole] = useDeactivateMyRoleMutation();

  const [updateStatus, setUpdateStatus] = useState<StatusType>("idle");

  const activeRoleIds = useMemo(
    () => new Set((myRolesData?.data?.roles ?? []).map((r) => r.id)),
    [myRolesData],
  );

  const tagsForBar: ShotTag[] = useMemo(
    () => (tagsData?.data?.tags ?? []).map(toShotTagShape),
    [tagsData],
  );

  const handleSelectTag = useCallback((tagId: string | null) => {
    setSelectedTagIdStr(tagId);
  }, []);

  const handleToggle = useCallback(
    async (roleId: number, isCurrentlyActive: boolean) => {
      try {
        setUpdateStatus("saving");
        const mutate = isCurrentlyActive ? deactivateMyRole : activateMyRole;
        await mutate(roleId).unwrap();
        setUpdateStatus("saved");
        setTimeout(() => setUpdateStatus("idle"), 1000);
      } catch (e) {
        console.error("Failed to toggle role:", e);
        setUpdateStatus("error");
        setTimeout(() => setUpdateStatus("idle"), 3000);
      }
    },
    [activateMyRole, deactivateMyRole],
  );

  if (isLoadingMyRoles) {
    return <SvgLoadingAnimation />;
  }

  const roles = rolesData?.data?.roles ?? [];

  return (
    <>
      <div className="u-col u-w100 u-gap16">
        <TitleBlock
          title="ロール"
          description="自分が担えるロールを設定します"
        />

        <FilterChipBar
          items={tagsForBar}
          selectedId={selectedTagIdStr}
          onSelect={handleSelectTag}
        />

        {isFetchingRoles && roles.length === 0 ? (
          <EmptyState>ロールを読み込み中...</EmptyState>
        ) : roles.length === 0 ? (
          <EmptyState>該当するロールがありません</EmptyState>
        ) : (
          <div className="u-col u-w100 u-gap16">
            {roles.map((role) => {
              const isActive = activeRoleIds.has(role.id);
              const statusLabel = roleStatusLabel(
                role.accept_position,
                role.allow_subcontracting,
              );
              return (
                <RadioCard
                  key={role.id}
                  name="role"
                  id={role.id}
                  width="128px"
                  height="128px"
                  showImage
                  showRadio={false}
                  checked={isActive}
                  onClick={() => handleToggle(role.id, isActive)}
                >
                  <div className="u-col u-gap4 u-w100">
                    <RoleCardTitleRow>
                      <label className="heading-13">{role.role_name_jp}</label>
                      {statusLabel && (
                        <StatusBadge
                          variant="inline"
                          label={statusLabel}
                          highlighted
                        />
                      )}
                    </RoleCardTitleRow>
                    {role.description && (
                      <RoleCardDescription>{role.description}</RoleCardDescription>
                    )}
                  </div>
                </RadioCard>
              );
            })}
          </div>
        )}
      </div>

      <StatusIndicator status={updateStatus} />
    </>
  );
};

export default Role;
